import React, { useState } from "react";
import {
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  Paperclip,
  Plus,
  Send,
  Type,
  Underline as UnderlineIcon,
  Code,
} from "lucide-react";
import RichMessageEditor from "./RichMessageEditor";
import EmojiInsertButton from "../EmojiInsertButton";
import GifPickerButton from "./GifPickerButton";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

function FormatStrip({ composerRef, disabled }) {
  const run = (fn) => {
    const editor = composerRef.current?.editor;
    if (!editor || disabled) return;
    fn(editor);
  };

  const btn =
    "inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50";

  return (
    <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide px-1 pb-1">
      <button
        type="button"
        className={btn}
        disabled={disabled}
        aria-label="Bold"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => run((ed) => ed.chain().focus().toggleBold().run())}
      >
        <Bold className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={btn}
        disabled={disabled}
        aria-label="Italic"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => run((ed) => ed.chain().focus().toggleItalic().run())}
      >
        <Italic className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={btn}
        disabled={disabled}
        aria-label="Underline"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => run((ed) => ed.chain().focus().toggleUnderline().run())}
      >
        <UnderlineIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={btn}
        disabled={disabled}
        aria-label="Bullet list"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => run((ed) => ed.chain().focus().toggleBulletList().run())}
      >
        <List className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={btn}
        disabled={disabled}
        aria-label="Numbered list"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          run((ed) => ed.chain().focus().toggleOrderedList().run())
        }
      >
        <ListOrdered className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={btn}
        disabled={disabled}
        aria-label="Link"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const editor = composerRef.current?.editor;
          if (!editor || disabled) return;
          const prev = editor.getAttributes("link").href || "";
          const next = window.prompt("Link URL", prev || "https://");
          if (next === null) return;
          const trimmed = next.trim();
          if (!trimmed) {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
          }
          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: trimmed })
            .run();
        }}
      >
        <Link2 className="h-4 w-4" />
      </button>
      <button
        type="button"
        className={btn}
        disabled={disabled}
        aria-label="Code"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => run((ed) => ed.chain().focus().toggleCode().run())}
      >
        <Code className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Single-row mobile DM composer: [+] · input · send.
 * Attach / GIF / emoji / formatting live in a bottom sheet (or format strip).
 */
export default function MobileMessageComposer({
  composerRef,
  editorKey,
  value,
  onChange,
  onUserInput,
  onSubmit,
  onPaste,
  disabled = false,
  sendDisabled = false,
  placeholder = "Write a message…",
  referenceContext = null,
  /** Workplace GIF picker needs orgId; social passes social */
  orgId = null,
  social = false,
  onAttach = null,
  attachDisabled = false,
  onGifSelect = null,
  gifDisabled = false,
  onEmojiInsert = null,
  onEditorKeyDown = null,
  className,
}) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);

  const closeTools = () => setToolsOpen(false);

  return (
    <div className={cn("flex w-full min-w-0 flex-col gap-1", className)}>
      {formatOpen ? (
        <FormatStrip composerRef={composerRef} disabled={disabled} />
      ) : null}
      <div
        onPaste={onPaste}
        className="flex w-full min-w-0 items-end gap-1.5 rounded-full border border-border bg-muted/40 px-1.5 py-1"
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full"
          disabled={disabled}
          aria-label="Message tools"
          onClick={() => setToolsOpen(true)}
        >
          <Plus className="h-5 w-5" />
        </Button>
        <RichMessageEditor
          key={editorKey}
          ref={composerRef}
          value={value}
          onChange={onChange}
          onUserInput={onUserInput}
          onSubmit={onSubmit}
          disabled={disabled}
          placeholder={placeholder}
          minRows={1}
          variant="compact"
          toolbar="never"
          chrome="borderless"
          className="min-w-0 flex-1"
          referenceContext={referenceContext}
          onEditorKeyDown={onEditorKeyDown}
        />
        <Button
          type="button"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full"
          disabled={sendDisabled || disabled}
          title="Send"
          aria-label="Send"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onSubmit}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>

      <Sheet open={toolsOpen} onOpenChange={setToolsOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl pb-[max(1rem,env(safe-area-inset-bottom,0px))]"
        >
          <SheetHeader className="text-left pb-2">
            <SheetTitle>Add to message</SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-2 gap-2 pt-1">
            {onAttach ? (
              <Button
                type="button"
                variant="outline"
                className="h-12 justify-start gap-2"
                disabled={attachDisabled || disabled}
                onClick={() => {
                  closeTools();
                  onAttach();
                }}
              >
                <Paperclip className="h-4 w-4" />
                Attach
              </Button>
            ) : null}
            {onGifSelect ? (
              <div className="flex h-12 items-center justify-start rounded-md border border-input px-3">
                <GifPickerButton
                  orgId={orgId}
                  social={social}
                  disabled={gifDisabled || disabled}
                  onSelect={(gif) => {
                    closeTools();
                    onGifSelect(gif);
                  }}
                />
                <span className="ml-2 text-sm">GIF</span>
              </div>
            ) : null}
            {onEmojiInsert ? (
              <div className="flex h-12 items-center justify-start rounded-md border border-input px-3">
                <EmojiInsertButton
                  disabled={disabled}
                  onInsert={(native) => {
                    closeTools();
                    onEmojiInsert(native);
                  }}
                />
                <span className="ml-2 text-sm">Emoji</span>
              </div>
            ) : null}
            <Button
              type="button"
              variant="outline"
              className="h-12 justify-start gap-2"
              disabled={disabled}
              onClick={() => {
                closeTools();
                setFormatOpen((v) => !v);
              }}
            >
              <Type className="h-4 w-4" />
              {formatOpen ? "Hide formatting" : "Formatting"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
